import { useCallback, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useEventLogger } from '../../../lib/eventLogger/EventLoggerProvider';
import { usePageFlow } from '../../../lib/pageFlow';

export interface UseDeleteDraftNavigationResult {
    /** Pass to `DraftApplicationTabs`/`JobInfoHeader` `onDelete` — awaits the delete, then advances. */
    onDelete: (deletionPromise: Promise<unknown>) => Promise<void>;
    /** Pass to `DraftApplicationTabs`/`JobInfoHeader` `isDeleting` — true while a delete is in flight. */
    isDeleting: boolean;
}

/**
 * Delete-a-draft-and-advance behavior for the review spine, shared by ReviewPage and QuestionsPage.
 *
 * The trash button lives in the shared `JobInfoHeader`, which fires the delete mutation itself and
 * hands back the promise; this hook owns what the page must then do — flip the in-flight flag, drop
 * the draft from the `draftIds` param, and walk to the next draft the same questions-aware way the
 * rest of the flow does (a draft with unfilled screener questions opens its `/questions` step ahead
 * of `/review`). Centralizing it keeps both pages' delete experience identical and stops the
 * next-draft route from hardcoding `/review`, which stranded a next draft that still had questions.
 */
export const useDeleteDraftNavigation = (): UseDeleteDraftNavigationResult => {
    const navigate = useNavigate();
    const location = useLocation();
    const { draftId } = useParams<{ draftId: string }>();
    const [searchParams] = useSearchParams();
    const eventLogger = useEventLogger();
    const { allSteps } = usePageFlow();
    const [isDeleting, setIsDeleting] = useState(false);

    const onDelete = useCallback(
        async (deletionPromise: Promise<unknown>) => {
            setIsDeleting(true);
            try {
                await deletionPromise;

                const draftIds = searchParams.get('draftIds')?.split(',').filter(Boolean) || [];
                const newDraftIds = draftIds.filter((id) => id !== draftId);

                eventLogger.logReviewDraftAction?.({
                    currentPage: location.pathname,
                    action: 'delete-draft',
                    draftId: draftId || '',
                    remainingDraftCount: newDraftIds.length,
                });

                const newParams = new URLSearchParams(searchParams);
                // Drop the param entirely once the last draft is deleted, rather than leaving a
                // dangling `?draftIds=`. ConfirmationPage reads it as `[]` either way, but a clean URL
                // avoids the empty-value noise.
                if (newDraftIds.length > 0) {
                    newParams.set('draftIds', newDraftIds.join(','));
                } else {
                    newParams.delete('draftIds');
                }
                const newSearch = `?${newParams.toString()}`;

                // Advance to the step right after this draft's review in the flow's step list.
                // `allSteps[reviewIndex + 1]` IS the next draft's first step — its `/questions` when
                // still gated, else its `/review` (the flow already made that decision) — so this
                // resolves the next draft's first step, not just a blanket fallback. `/confirmation`
                // is reached only when this is the last draft (the step after its review) or, as a
                // defensive fallback, if this draft's review isn't in `allSteps`. Reusing `allSteps`
                // keeps the questions-vs-review decision in the flow (mirrors `nextDraftDestination`).
                const reviewIndex = allSteps.findIndex((step) =>
                    step.routes.includes(`/auto-apply/${draftId}/review`),
                );
                const nextStep = reviewIndex >= 0 ? allSteps[reviewIndex + 1] : undefined;
                const nextRoute = nextStep?.routes[0] ?? '/auto-apply/confirmation';

                void navigate(`${nextRoute}${newSearch}`, { replace: true });
            } catch {
                // The delete failed server-side — the draft still exists, so leave it in the batch
                // and stay on the page rather than optimistically removing/advancing past a draft
                // the server still holds. (The button re-enables via `finally`.)
            } finally {
                setIsDeleting(false);
            }
        },
        [draftId, searchParams, eventLogger, location.pathname, allSteps, navigate],
    );

    return { onDelete, isDeleting };
};
